import { useGetOrganizationPricingHoldbacksQuery } from '@/services/pricing';
import { StandardHoldbackSelfServiceClientData } from '@/types/Holdbacks';
import { Box } from '@palmetto/palmetto-components';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { PriceSheetStatus, ProgramType } from 'types';

import { ActiveStandardHoldbacks } from './ActiveStandardHoldbacks';
import { ArchivedStandardHoldbacks } from './ArchivedStandardHoldbacks';

export const StandardHoldbacksTab = () => {
  const { alias = '', programType = '' } = useParams<{ alias: string; programType: ProgramType }>();

  const { data, isLoading, isFetching } = useGetOrganizationPricingHoldbacksQuery(
    { alias, programType },
    { skip: !alias || !programType },
  );

  const holdbacks = useMemo(() => {
    if (!data?.holdbacks?.length) return [];

    const sortByStateAndDate = (
      a: StandardHoldbackSelfServiceClientData,
      b: StandardHoldbackSelfServiceClientData,
    ) => {
      // First sort by state alphabetically
      const stateComparison = a.state.localeCompare(b.state);
      if (stateComparison !== 0) return stateComparison;

      // Then by created date descending (newest first)
      const bTime = b.createdBy?.date ? new Date(b.createdBy.date).getTime() : 0;
      const aTime = a.createdBy?.date ? new Date(a.createdBy.date).getTime() : 0;
      return bTime - aTime;
    };

    return (data.holdbacks || []).toSorted(sortByStateAndDate);
  }, [data]);

  const activeHoldbacks = useMemo(
    () => holdbacks.filter((h: StandardHoldbackSelfServiceClientData) => h.status === PriceSheetStatus.active),
    [holdbacks],
  );

  const archivedHoldbacks = useMemo(
    () => holdbacks.filter((h: StandardHoldbackSelfServiceClientData) => h.status === PriceSheetStatus.archived),
    [holdbacks],
  );

  return (
    <Box flex="auto" gap="lg">
      <ActiveStandardHoldbacks
        activeHoldbacks={activeHoldbacks}
        alias={alias}
        programType={programType as ProgramType}
        isLoading={isLoading || isFetching}
      />
      <ArchivedStandardHoldbacks holdbacks={archivedHoldbacks} isLoading={isLoading || isFetching} />
    </Box>
  );
};
